import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles, TrendingUp, MousePointerClick } from "lucide-react";

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen pt-32 pb-20 overflow-hidden noise-bg"
    >
      {/* gradient orbs */}
      <div className="orb top-20 -left-32 w-[520px] h-[520px] bg-indigo-700/40" />
      <div className="orb top-40 right-[-160px] w-[600px] h-[600px] bg-blue-600/30" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(67,56,202,0.18),transparent_55%)]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          {/* Left - Copy */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 glass rounded-full pl-2 pr-4 py-1.5 text-xs text-zinc-300"
            >
              <span className="h-5 w-5 grid place-items-center rounded-full bg-blue-500/20">
                <Sparkles className="h-3 w-3 text-blue-300" />
              </span>
              <span>Trusted by 120+ global D2C brands</span>
              <span className="h-1.5 w-1.5 rounded-full bg-green-400 pulse-dot ml-1" />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[1.02] text-white"
            >
              <span className="text-gradient">CRO Optimized</span>{" "}
              Shopify Stores &<br className="hidden sm:block" />{" "}
              Performance Marketing<br className="hidden sm:block" />{" "}
              That Scale Globally.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="mt-6 max-w-2xl text-base md:text-lg text-zinc-400 leading-relaxed"
            >
              Kavion Global Limited helps eCommerce brands increase conversions, maximize ROAS,
              and scale profitably through world-class Shopify design and performance marketing.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="mt-9 flex flex-col sm:flex-row gap-3"
            >
              <Link
                to="/contact#book"
                data-testid="hero-strategy-call-button"
                className="group relative inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 transition-all duration-300 hover:scale-[1.02] text-white rounded-full px-7 py-4 font-semibold glow-blue"
              >
                Book Free Strategy Call
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to="/contact?type=audit"
                data-testid="hero-audit-button"
                className="group inline-flex items-center justify-center gap-2 glass hover:bg-white/[0.07] transition-all text-white rounded-full px-7 py-4 font-semibold"
              >
                Get Free Store Audit
                <MousePointerClick className="h-4 w-4 text-blue-300" />
              </Link>
            </motion.div>

            {/* Stats strip */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.35 }}
              className="mt-14 grid grid-cols-3 gap-4 max-w-2xl"
            >
              {[
                { v: "$240M+", l: "Revenue Driven" },
                { v: "+312%", l: "Avg ROAS Lift" },
                { v: "120+", l: "Brands Scaled" },
              ].map((s) => (
                <div key={s.l} className="glass rounded-2xl p-4 sm:p-5">
                  <div className="font-display text-2xl sm:text-3xl font-extrabold text-white">{s.v}</div>
                  <div className="text-xs text-zinc-500 mt-1 uppercase tracking-wider">{s.l}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right - Floating dashboard mockup */}
          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              {/* Main analytics card */}
              <div className="relative glass-heavy rounded-3xl p-6 shadow-2xl">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-xs text-zinc-500 uppercase tracking-wider">Performance — Last 30 Days</div>
                    <div className="font-display text-2xl font-extrabold text-white mt-1">$1,284,910</div>
                  </div>
                  <span className="inline-flex items-center gap-1 text-xs text-green-400 bg-green-500/10 border border-green-500/20 px-2.5 py-1 rounded-full">
                    <TrendingUp className="h-3 w-3" /> +47.2%
                  </span>
                </div>
                {/* Bar chart */}
                <div className="flex items-end gap-1.5 h-32 mt-2">
                  {[40, 55, 38, 65, 50, 72, 60, 80, 70, 88, 76, 95].map((h, i) => (
                    <div key={i} className="flex-1 rounded-t-md bg-gradient-to-t from-blue-600/30 to-blue-400 relative overflow-hidden" style={{ height: `${h}%` }}>
                      <div className="absolute inset-0 bg-gradient-to-t from-transparent to-white/20" />
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-3 mt-5 pt-5 border-t border-white/5">
                  {[
                    { l: "ROAS", v: "6.8x" },
                    { l: "CVR", v: "4.2%" },
                    { l: "AOV", v: "$184" },
                  ].map((m) => (
                    <div key={m.l}>
                      <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{m.l}</div>
                      <div className="font-display text-lg font-bold text-white">{m.v}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Floating mini card */}
              <motion.div
                initial={{ opacity: 0, x: -20, y: 20 }}
                animate={{ opacity: 1, x: 0, y: 0 }}
                transition={{ duration: 0.8, delay: 0.55 }}
                className="absolute -left-6 sm:-left-10 bottom-12 glass-heavy rounded-2xl p-4 w-52 shadow-2xl hidden sm:block"
              >
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Conversion Rate</div>
                <div className="font-display text-2xl font-extrabold text-white mt-1">+248%</div>
                <svg viewBox="0 0 120 30" className="w-full h-8 mt-2">
                  <path d="M0 25 Q 20 22, 30 18 T 60 12 T 90 6 T 120 2" stroke="url(#g1)" strokeWidth="2" fill="none" />
                  <defs>
                    <linearGradient id="g1" x1="0" x2="1">
                      <stop offset="0%" stopColor="#60A5FA" />
                      <stop offset="100%" stopColor="#3B82F6" />
                    </linearGradient>
                  </defs>
                </svg>
              </motion.div>

              {/* Floating top right card */}
              <motion.div
                initial={{ opacity: 0, x: 20, y: -10 }}
                animate={{ opacity: 1, x: 0, y: 0 }}
                transition={{ duration: 0.8, delay: 0.65 }}
                className="absolute -right-4 -top-6 glass-heavy rounded-2xl p-3 pr-4 flex items-center gap-3 shadow-2xl"
              >
                <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-400 to-green-600 grid place-items-center">
                  <TrendingUp className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider">New Order</div>
                  <div className="text-sm font-bold text-white">+ $384.00</div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
